/*
 * Copyright (c) 2021-2021. Datafood srl
 * Developed by Revo Digital - revodigital.it
 * ---
 * Author: leonardoviada
 * File: queries.gql.ts
 * Project: mle-client
 * Committed last: 2021/11/12 @ 847
 * ---
 */

import {gql} from '@apollo/client';

/**
 * Richiede etichetta a partire dal suo identificativo.
 * Contiente MpesDocument serializzato in bodyContent.
 * @type {DocumentNode}
 */
export const GET_LABEL_BY_ID = gql`
    query($id: Int!) {
        label(id: $id) {
            id
            title
            lastModified
            creationDate
            bodyContent
            user {
                id
                firstName
                lastName
                company {
                    id
                }
            }
        }
    }
`;

/**
 * Richiede etichette a partire dall'identificativo del loro creatore (user associato).
 * @type {DocumentNode}
 */
export const GET_LABEL_BY_USER_ID = gql`
    query($userId: Int!) {
        labels(userId: $userId) {
            id
            title
            lastModified
            creationDate
            bodyContent
            user {
                id
                firstName
                lastName
            }
        }
    }
`;

/**
 * Richiede utente a partire dal suo identificativo.
 * Non contiene le credenziali di accesso nè l'email, che sono invece gestiti
 * dall'AuthProvider (REST) con l'entità AuthEntity.
 * @type {DocumentNode}
 */
export const GET_USER_BY_ID = gql`
    query($id: Int!) {
        user(id: $id) {
            id
            firstName
            lastName
            phone
            company {
                id
                name
                taxCode
                fiscalCode
                reaCode
                pec
                email
                address
            }
        }
    }
`;

/**
 * Richiede tutte le immagini di una compagnia a partire dall'identificativo della compagnia
 */
export const GET_IMAGES_BY_COMPANY_ID = gql`
    query ($id: Int!) {
        company(id: $id) {
            images {
                id
                bucketURL
                content
            }
        }
    }
`;

/**
 * Richiede le informazioni aggiuntive di un'immagine
 */
export const IMAGE_BY_ID = gql`
    query($id: Int!) {
        image(id: $id) {
            content
            bucketURL
        }
    }
`;

/**
 * Get all dataorigins by company id
 */
export const GET_DATAORIGINS_BY_COMPANY_ID = gql`
    query ($id: Int!) {
        company(id: $id) {
            dataOrigins {
                id
                name
                type
                creationDate
                contentType
                resource
            }
        }
    }
`;

export const GET_DATAORIGIN_BY_ID = gql`
    query ($id: Int!) {
        dataOrigin(id: $id) {
            name
            company {
                id
            }
            resource
            hasCredentials
            creationDate
            contentType
            type
        }
    }
`;

export const GET_AVAILABLE_RECIPES = gql`
    query($companyId: Int!) {
        getMyLabelRecipesMeta(companyId: $companyId) {
            id
            name
        }
    }
`;

export const GET_REVISIONS_INFO = gql`
    query($companyId: Int!) {
        getLabelRevisions(companyId: $companyId) {
            id
            company_id
            recipe_name
            version_number
            subversion_number
        }
    }
`

export const GET_REVISION_INFO = gql`
    query ($revisionId: Int!, $companyId: Int!) {
        getLabelRevision(companyId: $companyId, revisionId: $revisionId) {
            id
            recipe_name
            recipe_code
            version_number
            subversion_number
        }
    }
`

export const GET_AVAILABLE_DY_GROUPS = gql`
    query ($revisionId: Int!, $companyId: Int!) {
        getAvailableDynamicGroups(companyId: $companyId, revisionId: $revisionId) {
            groupName
            keys {
                key
                value
            }
        }
    }
`

export const COMPUTE_DY_FIELD = gql`
    mutation ($revisionId: Int!, $companyId: Int!, $key: String!) {
        computeDynamicOriginValue(companyId: $companyId, revisionId: $revisionId, key: $key)
    }
`

export const COMPUTE_DY_FIELDS = gql`
    mutation ($revisionId: Int!, $companyId: Int!, $keys: [String!]!) {
        computeDynamicOriginValues(companyId: $companyId, revisionId: $revisionId, keys: $keys) {
            key
            value
        }
    }
`;

export const GET_AVAILABLE_DYNAMICS = gql`
    query($companyId: Int!, $revisionId: Int!) {
        getAvailableDynamicKeys(revisionId: $revisionId, companyId: $companyId)
    }
`

export const GET_RECIPE_NAME_BY_ID = gql`
    query($companyId: Int!, $labelId: Int!) {
        getMyLabelRecipe(labelId: $labelId, companyId: $companyId) {
            id
            main
        }
    }
`;

export const GET_COMPANY_META = gql`
    query ($companyId: Int!) {
        getCompanyMeta(companyId: $companyId) {
            id
            name
        }
    }
`;

export const GET_AVAILABLE_ROOTS = gql`
    query {
        getAvailableRecipeRootKeys
    }
`;

export const GET_RECIPE_BY_ID = gql`
    query($companyId: Int!, $labelId: Int!) {
        getMyLabelRecipe(labelId: $labelId, companyId: $companyId) {
            id
            main
            required
            energy
            nutrients
            ingredients
            allergens
            portions
            processing
            complementary
            meta
        }
    }
`;

export const GET_COMPANIES_META = gql`
    query {
        getCompaniesMeta {
            id
            name
        }
    }
`;

export const GET_BUILTINS_META = gql`
    query ($id: Int!) {
        getBuiltinMetaForLabel(id: $id) {
            name
            value
        }
    }
`;